import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, ButtonIcon, Checkbox, Input, Select } from '@resideo/blueprint-oneportal';
import type { RecordingBlock, DayOfWeek, EventRecordingModeType } from '../types';
import { EventRecordingMode } from '../types';
import {
  isValidTimeRange,
  generateBlockId,
  ALL_DAYS,
  getDayLabel,
} from '../recordingScheduleUtils';
import {
  DEFAULT_RECORDING_START_TIME,
  DEFAULT_RECORDING_END_TIME,
  ALL_DAY_START_TIME,
  ALL_DAY_END_TIME,
} from '../constants';
import { closeIcon, deleteIcon } from '@/assets/icons';
import {
  PopupOverlay,
  PopupContainer,
  PopupHeader,
  PopupTitle,
  TimeRow,
  TimeFieldGroup,
  TimeFieldLabel,
  AllDayRow,
  AllDayLabel,
  ModeField,
  ModeLabel,
  DaysSection,
  DaysSectionLabel,
  DaysToggleRow,
  DayToggle,
  PopupFooter,
} from '../styled/Recording.styled';

interface AddRecordingBlockPopupProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (block: RecordingBlock) => void;
  editBlock?: RecordingBlock | null;
  onDelete?: (blockId: string) => void;
  /** All existing recording blocks (used to disable days with overlapping times) */
  existingBlocks?: RecordingBlock[];
  /** Pre-populate time/day from drag selection on the calendar */
  initialValues?: {
    startTime?: string; // "HH:MM" 24h
    endTime?: string;   // "HH:MM" 24h
    day?: DayOfWeek;
  } | null;
}

/** Validate a 24h time string (H:MM or HH:MM, 0:00–23:59) */
function isValid24hTime(value: string): boolean {
  const match = value.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return false;
  const h = Number(match[1]);
  const m = Number(match[2]);
  return h >= 0 && h <= 23 && m >= 0 && m <= 59;
}

/** Check if two time ranges overlap */
function timeRangesOverlap(
  start1: string, end1: string,
  start2: string, end2: string,
): boolean {
  const toMin = (t: string) => {
    const [h, m] = t.split(':').map(Number);
    return h * 60 + m;
  };
  const s1 = toMin(start1);
  const e1 = toMin(end1);
  const s2 = toMin(start2);
  const e2 = toMin(end2);
  return s1 < e2 && s2 < e1;
}

const AddRecordingBlockPopup: React.FC<AddRecordingBlockPopupProps> = ({
  isOpen,
  onClose,
  onSave,
  editBlock,
  onDelete,
  existingBlocks = [],
  initialValues,
}) => {
  const { t } = useTranslation();

  const [startTime, setStartTime] = useState(DEFAULT_RECORDING_START_TIME);
  const [endTime, setEndTime] = useState(DEFAULT_RECORDING_END_TIME);
  const lastValidStartTime = useRef(DEFAULT_RECORDING_START_TIME);
  const lastValidEndTime = useRef(DEFAULT_RECORDING_END_TIME);
  const [isAllDay, setIsAllDay] = useState(false);
  const [continuousRecording, setContinuousRecording] = useState(true);
  const [eventMode, setEventMode] = useState<EventRecordingModeType>(EventRecordingMode.ENABLED);
  const [selectedDays, setSelectedDays] = useState<DayOfWeek[]>([...ALL_DAYS]);
  const [sourceDay, setSourceDay] = useState<DayOfWeek | null>(null);

  // Initialize from editBlock when opening
  useEffect(() => {
    if (isOpen && editBlock) {
      setStartTime(editBlock.startTime);
      setEndTime(editBlock.endTime);
      lastValidStartTime.current = editBlock.startTime;
      lastValidEndTime.current = editBlock.endTime;
      setContinuousRecording(editBlock.continuousRecording);
      // Derive event mode from clipEventRecording + snapshot
      if (editBlock.clipEventRecording && editBlock.snapshot) {
        setEventMode(EventRecordingMode.ENABLED);
      } else if (!editBlock.clipEventRecording && editBlock.snapshot) {
        setEventMode(EventRecordingMode.SNAPSHOT_ONLY);
      } else {
        setEventMode(EventRecordingMode.DISABLED);
      }
      // If a specific day was right-clicked, select only that day
      setSelectedDays(initialValues?.day ? [initialValues.day] : [...editBlock.daysOfWeek]);
      setSourceDay(initialValues?.day || null);
      setIsAllDay(editBlock.startTime === ALL_DAY_START_TIME && editBlock.endTime === ALL_DAY_END_TIME);
    } else if (isOpen && !editBlock) {
      // Use initialValues from drag selection if available, otherwise defaults
      const initStart = initialValues?.startTime || DEFAULT_RECORDING_START_TIME;
      const initEnd = initialValues?.endTime || DEFAULT_RECORDING_END_TIME;
      setStartTime(initStart);
      setEndTime(initEnd);
      lastValidStartTime.current = initStart;
      lastValidEndTime.current = initEnd;
      setIsAllDay(false);
      setContinuousRecording(true);
      setEventMode(EventRecordingMode.ENABLED);
      setSelectedDays(initialValues?.day ? [initialValues.day] : [...ALL_DAYS]);
      setSourceDay(initialValues?.day || null);
    }
  }, [isOpen, editBlock, initialValues]);

  if (!isOpen) return null;

  const handleAllDayToggle = () => {
    const next = !isAllDay;
    setIsAllDay(next);
    if (next) {
      setStartTime(ALL_DAY_START_TIME);
      setEndTime(ALL_DAY_END_TIME);
      lastValidStartTime.current = ALL_DAY_START_TIME;
      lastValidEndTime.current = ALL_DAY_END_TIME;
    }
  };

  const toggleDay = (day: DayOfWeek) => {
    setSelectedDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]
    );
  };

  const handleStartTimeChange = (value: string) => {
    setStartTime(value);
    if (isValid24hTime(value)) {
      lastValidStartTime.current = value;
    }
  };

  const handleEndTimeChange = (value: string) => {
    setEndTime(value);
    if (isValid24hTime(value)) {
      lastValidEndTime.current = value;
    }
  };

  const handleStartTimeBlur = () => {
    if (!isValid24hTime(startTime)) {
      setStartTime(lastValidStartTime.current);
    }
  };

  const handleEndTimeBlur = () => {
    if (!isValid24hTime(endTime)) {
      setEndTime(lastValidEndTime.current);
    }
  };

  const handleSave = () => {
    if (!isAllDay && !isValidTimeRange(startTime, endTime)) {
      return; // Invalid time range
    }

    if (selectedDays.length === 0) {
      return; // At least one day required
    }

    const block: RecordingBlock = {
      id: editBlock?.id || generateBlockId(),
      startTime: isAllDay ? ALL_DAY_START_TIME : startTime,
      endTime: isAllDay ? ALL_DAY_END_TIME : endTime,
      continuousRecording,
      clipEventRecording: eventMode === EventRecordingMode.ENABLED,
      snapshot: eventMode === EventRecordingMode.ENABLED || eventMode === EventRecordingMode.SNAPSHOT_ONLY,
      daysOfWeek: selectedDays.filter((d) => !overlappingDays.has(d)),
    };

    onSave(block);
  };

  const isTimeValid = isAllDay || isValidTimeRange(startTime, endTime);
  const hasDays = selectedDays.length > 0;
  const canSave = isTimeValid && hasDays;

  // Determine which days already have an overlapping block (exclude the block being edited)
  const overlappingDays = new Set<DayOfWeek>();
  const currentStart = isAllDay ? ALL_DAY_START_TIME : startTime;
  const currentEnd = isAllDay ? ALL_DAY_END_TIME : endTime;
  if (isValid24hTime(currentStart) && isValid24hTime(currentEnd)) {
    for (const block of existingBlocks) {
      if (editBlock && block.id === editBlock.id) continue;
      if (timeRangesOverlap(currentStart, currentEnd, block.startTime, block.endTime)) {
        for (const day of block.daysOfWeek) {
          if (day !== sourceDay) {
            overlappingDays.add(day);
          }
        }
      }
    }
  }

  return (
    <PopupOverlay onClick={onClose} data-testid="recording-popup-overlay">
      <PopupContainer onClick={(e) => e.stopPropagation()} data-testid="recording-popup">
        <PopupHeader>
          <PopupTitle>
            {editBlock ? t('recording.editRecordingBlock') : t('recording.addRecordingBlock')}
          </PopupTitle>
          {editBlock && onDelete ? (
            <ButtonIcon
              size="small"
              iconType="icon"
              onClick={() => onDelete(editBlock.id)}
              aria-label={t('recording.deleteBlock')}
              data-testid="delete-block-btn"
            >
              <img src={deleteIcon} width="24" height="24" alt="" aria-hidden="true" />
            </ButtonIcon>
          ) : (
            <ButtonIcon
              size="small"
              iconType="icon"
              onClick={onClose}
              aria-label={t('common.close')}
              data-testid="popup-close-btn"
            >
              <img src={closeIcon} width="16" height="16" alt="" aria-hidden="true" />
            </ButtonIcon>
          )}
        </PopupHeader>

        {/* Time Begin / Time End */}
        <TimeRow>
          <TimeFieldGroup>
            <TimeFieldLabel>{t('recording.timeBegin')}</TimeFieldLabel>
            <Input
              type="text"
              value={startTime}
              onChange={(e) => handleStartTimeChange(e.target.value)}
              onBlur={handleStartTimeBlur}
              disabled={isAllDay}
              placeholder={t('recording.timePlaceholder')}
              data-testid="start-time-input"
            />
          </TimeFieldGroup>
          <TimeFieldGroup>
            <TimeFieldLabel>{t('recording.timeEnd')}</TimeFieldLabel>
            <Input
              type="text"
              value={endTime}
              onChange={(e) => handleEndTimeChange(e.target.value)}
              onBlur={handleEndTimeBlur}
              disabled={isAllDay}
              placeholder={t('recording.timePlaceholder')}
              data-testid="end-time-input"
            />
          </TimeFieldGroup>
        </TimeRow>

        {/* All Day */}
        <AllDayRow>
          <Checkbox
            checked={isAllDay}
            onChange={handleAllDayToggle}
            data-testid="all-day-checkbox"
          />
          <AllDayLabel>{t('recording.allDay')}</AllDayLabel>
        </AllDayRow>

        {/* Continuous Recording Mode */}
        <ModeField>
          <ModeLabel>{t('recording.continuousRecordingMode')}</ModeLabel>
          <Select
            value={continuousRecording ? 'true' : 'false'}
            onChange={(e) => setContinuousRecording(e.target.value === 'true')}
            data-testid="continuous-mode-select"
          >
            <option value="true">{t('common.enabled')}</option>
            <option value="false">{t('common.disabled')}</option>
          </Select>
        </ModeField>

        {/* Event Based Recording Mode */}
        <ModeField>
          <ModeLabel>{t('recording.eventBasedRecordingMode')}</ModeLabel>
          <Select
            value={eventMode}
            onChange={(e) => setEventMode(e.target.value as EventRecordingModeType)}
            data-testid="event-mode-select"
          >
            <option value={EventRecordingMode.ENABLED}>{t('recording.eventRecordingNotificationsShort')}</option>
            <option value={EventRecordingMode.SNAPSHOT_ONLY}>{t('recording.snapshotOnly')}</option>
            <option value={EventRecordingMode.DISABLED}>{t('common.disabled')}</option>
          </Select>
        </ModeField>

        {/* Apply to other days */}
        <DaysSection>
          <DaysSectionLabel>{t('recording.applyToOtherDays')}</DaysSectionLabel>
          <DaysToggleRow data-testid="days-toggle-row">
            {ALL_DAYS.map((day) => {
              const isSource = day === sourceDay;
              const isOverlapping = overlappingDays.has(day);
              const isDisabled = isSource || isOverlapping;
              return (
                <DayToggle
                  key={day}
                  $active={selectedDays.includes(day)}
                  $isSource={isSource}
                  $isDisabled={isDisabled}
                  onClick={() => !isDisabled && toggleDay(day)}
                  disabled={isDisabled}
                  data-testid={`day-toggle-${day}`}
                  type="button"
                >
                  {getDayLabel(day, t)}
                </DayToggle>
              );
            })}
          </DaysToggleRow>
        </DaysSection>

        {/* Footer */}
        <PopupFooter>
          <Button variant="secondary" size="small" onClick={onClose} data-testid="popup-cancel-btn">
            {t('common.cancel')}
          </Button>
          <Button variant="primary" size="small" onClick={handleSave} disabled={!canSave} data-testid="popup-save-btn">
            {t('common.save')}
          </Button>
        </PopupFooter>
      </PopupContainer>
    </PopupOverlay>
  );
};

export default AddRecordingBlockPopup;
